// Lista de feedbacks completados del alumno (vista alumno).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { StarRating } from '@/components/molecules/StarRating';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import { listMyFeedbacks, type Feedback } from '@/services/feedbacks';
import styles from './MisFeedbacks.module.scss';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${day.charAt(0).toUpperCase() + day.slice(1)} · ${time}`;
}

function average(f: Feedback): number | null {
  const scores = [f.pronunciation, f.fluency, f.structures, f.vocabulary].filter((n): n is number => n !== null);
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function MisFeedbacks() {
  const [items, setItems] = useState<Feedback[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMyFeedbacks()
      .then((rows) => { if (!cancelled) setItems(rows); })
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <Header title="Mis feedbacks" />
      <main className={styles.main}>
        <p className={styles.lead}>
          Las valoraciones que tus profes han publicado tras tus clases. Pincha en una fila para ver el detalle.
        </p>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : !items || items.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="star" size={32} />
            <h3>Aún no tienes feedbacks publicados</h3>
            <p>Cuando tu profe complete una valoración tras una clase, aparecerá aquí.</p>
          </div>
        ) : (
          <ul className={styles.list}>
            {items.map((f) => {
              const avg = average(f);
              return (
                <li key={f.id} className={styles.row}>
                  <div className={styles.rowDate}>{formatDate(f.booking_scheduled_at)}</div>
                  <div className={styles.rowBody}>
                    <div className={styles.rowHeader}>
                      <span className={styles.lessonTitle}>{f.lesson_title ?? 'Clase'}</span>
                      <span className={styles.teacherName}>con {f.teacher_name}</span>
                    </div>
                    <div className={styles.rowMeta}>
                      {f.attendance && (
                        <Badge tone={f.attendance === 'present' ? 'success' : f.attendance === 'late' ? 'warning' : 'neutral'}>
                          {f.attendance === 'present' ? 'Presente' : f.attendance === 'late' ? 'Tarde' : 'Ausente'}
                        </Badge>
                      )}
                      <Badge tone={f.booking_type === 'gr' ? 'accent' : 'neutral'}>
                        {f.booking_type === 'gr' ? 'Grupal' : 'Individual'}
                      </Badge>
                      {avg !== null && (
                        <span className={styles.avgWrap}>
                          <StarRating value={Math.round(avg)} size={14} label={`Promedio ${avg.toFixed(1)}`} />
                          <span className={styles.avgValue}>{avg.toFixed(1)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.rowActions}>
                    <Button asChild variant="secondary" size="sm">
                      <Link to={`/mis-feedbacks/${f.booking_id}`}>Ver</Link>
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </>
  );
}
